import { useState, useEffect } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export function PayPalPaymentButton({ 
  amount, 
  currency = 'USD',
  description = 'Susu Money Payment',
  paymentType = 'contribution',
  clubId = null,
  onSuccess,
  onError,
  onCancel
}) {
  const [clientId, setClientId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPayPalConfig();
  }, []);

  const fetchPayPalConfig = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/paypal/config`);
      if (response.ok) {
        const data = await response.json();
        setClientId(data.client_id);
      } else {
        setError('PayPal is not available');
      }
    } catch (err) {
      setError('Failed to load PayPal');
      console.error('PayPal config error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-stone-600">Loading PayPal...</span>
      </div>
    );
  }

  if (error || !clientId) {
    return (
      <div className="p-4 bg-red-50 rounded-xl text-center">
        <XCircle className="w-6 h-6 text-red-500 mx-auto mb-2" />
        <p className="text-sm text-red-600">{error || 'PayPal unavailable'}</p>
      </div>
    );
  }

  return (
    <PayPalScriptProvider options={{ 
      clientId, 
      currency,
      intent: 'capture'
    }}>
      <PayPalButtons
        style={{
          layout: 'vertical',
          color: 'gold',
          shape: 'rect',
          label: 'paypal'
        }}
        createOrder={(data, actions) => {
          // Use PayPal's client-side order creation
          return actions.order.create({
            purchase_units: [{
              amount: {
                value: amount.toFixed(2),
                currency_code: currency
              },
              description: description
            }]
          });
        }}
        onApprove={async (data, actions) => {
          try {
            // Capture the order on PayPal's side
            const details = await actions.order.capture();
            
            // Record the successful payment in our backend
            const token = localStorage.getItem('token');
            await fetch(`${API_BASE}/api/paypal/record-payment`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                paypal_order_id: data.orderID,
                amount: amount,
                currency: currency,
                payment_type: paymentType,
                club_id: clubId,
                payer_email: details.payer?.email_address,
                status: details.status
              })
            });
            
            toast.success('Payment successful!');
            if (onSuccess) {
              onSuccess(details);
            }
          } catch (err) {
            console.error('Payment capture error:', err);
            toast.error('Payment recording failed');
            if (onError) onError(err);
          }
        }}
        onCancel={() => {
          toast.info('Payment cancelled');
          if (onCancel) onCancel();
        }}
        onError={(err) => {
          console.error('PayPal error:', err);
          toast.error('Payment error occurred');
          if (onError) onError(err);
        }}
      />
    </PayPalScriptProvider>
  );
}

// Full PayPal Payment Card Component
export default function PayPalPayment({
  amount,
  currency = 'USD',
  description,
  paymentType = 'contribution',
  clubId,
  onSuccess,
  onClose
}) {
  const [status, setStatus] = useState('ready'); // ready, processing, success, error

  const handleSuccess = (result) => {
    setStatus('success');
    setTimeout(() => {
      if (onSuccess) onSuccess(result);
    }, 1500);
  };

  const handleError = (err) => {
    setStatus('error');
  };

  return (
    <Card className="max-w-md mx-auto" data-testid="paypal-payment-card">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-4">
          <svg className="w-24 h-8" viewBox="0 0 101 32" fill="none">
            <path d="M12.237 6.136h-6.59c-.45 0-.832.327-.903.77L2.514 21.09c-.053.328.202.625.537.625h3.148c.45 0 .833-.328.904-.77l.602-3.817c.07-.443.453-.77.903-.77h2.082c4.336 0 6.838-2.099 7.493-6.26.295-1.82.012-3.249-.842-4.25-.939-1.1-2.604-1.712-4.804-1.712zm.76 6.168c-.36 2.367-2.165 2.367-3.912 2.367h-.993l.697-4.414c.042-.265.27-.46.537-.46h.455c1.19 0 2.313 0 2.892.678.346.405.451 1.008.324 1.829z" fill="#003087"/>
            <path d="M35.092 12.232h-3.163c-.266 0-.494.195-.537.46l-.139.884-.221-.32c-.686-.996-2.214-1.329-3.74-1.329-3.5 0-6.49 2.651-7.072 6.37-.303 1.855.127 3.63 1.181 4.87.968 1.139 2.352 1.614 4.002 1.614 2.831 0 4.4-1.82 4.4-1.82l-.141.883c-.053.328.202.626.537.626h2.849c.45 0 .833-.328.904-.77l1.712-10.842c.053-.328-.203-.626-.572-.626zm-4.433 6.164c-.305 1.808-1.74 3.021-3.573 3.021-.92 0-1.656-.296-2.128-.856-.469-.557-.645-1.35-.497-2.231.284-1.793 1.743-3.048 3.548-3.048.9 0 1.63.299 2.112.865.484.572.676 1.37.538 2.249z" fill="#003087"/>
            <path d="M55.26 12.232h-3.179c-.3 0-.58.147-.752.395l-4.342 6.397-1.84-6.148c-.115-.384-.47-.644-.87-.644h-3.125c-.373 0-.634.363-.516.715l3.468 10.174-3.262 4.604c-.255.36.007.852.446.852h3.176c.297 0 .575-.144.748-.387l10.471-15.11c.25-.36-.014-.848-.423-.848z" fill="#003087"/>
            <path d="M67.807 6.136h-6.59c-.45 0-.832.327-.903.77l-2.23 14.184c-.053.328.202.625.537.625h3.397c.315 0 .583-.229.633-.539l.633-4.048c.07-.443.453-.77.903-.77h2.082c4.336 0 6.838-2.099 7.493-6.26.295-1.82.012-3.249-.842-4.25-.94-1.1-2.604-1.712-4.804-1.712zm.76 6.168c-.36 2.367-2.165 2.367-3.912 2.367h-.993l.697-4.414c.042-.265.27-.46.537-.46h.455c1.19 0 2.313 0 2.892.678.346.405.451 1.008.324 1.829z" fill="#0070E0"/>
            <path d="M90.663 12.232H87.5c-.266 0-.494.195-.537.46l-.139.884-.221-.32c-.686-.996-2.214-1.329-3.74-1.329-3.5 0-6.49 2.651-7.072 6.37-.303 1.855.127 3.63 1.181 4.87.968 1.139 2.352 1.614 4.002 1.614 2.831 0 4.4-1.82 4.4-1.82l-.141.883c-.053.328.202.626.537.626h2.849c.45 0 .833-.328.904-.77l1.712-10.842c.052-.328-.203-.626-.572-.626zm-4.433 6.164c-.305 1.808-1.74 3.021-3.573 3.021-.92 0-1.656-.296-2.128-.856-.469-.557-.645-1.35-.497-2.231.284-1.793 1.743-3.048 3.548-3.048.9 0 1.63.299 2.112.865.484.572.676 1.37.538 2.249z" fill="#0070E0"/>
            <path d="M95.342 6.535l-2.263 14.4c-.053.328.202.625.537.625h2.727c.45 0 .833-.328.904-.77l2.23-14.185c.053-.327-.202-.625-.537-.625h-3.061c-.267 0-.495.196-.537.555z" fill="#0070E0"/>
          </svg>
        </div>
        <CardTitle className="text-lg">
          {status === 'success' ? 'Payment Complete!' : `Pay $${amount.toFixed(2)}`}
        </CardTitle>
        {description && status === 'ready' && (
          <p className="text-sm text-stone-500 mt-1">{description}</p>
        )}
      </CardHeader>
      
      <CardContent>
        {status === 'ready' && (
          <PayPalPaymentButton
            amount={amount}
            currency={currency}
            description={description}
            paymentType={paymentType}
            clubId={clubId}
            onSuccess={handleSuccess}
            onError={handleError}
          />
        )}
        
        {status === 'success' && (
          <div className="text-center py-6">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <p className="text-green-700 font-medium">Payment successful!</p>
            <p className="text-sm text-stone-500 mt-2">Redirecting...</p>
          </div>
        )}
        
        {status === 'error' && (
          <div className="text-center py-6">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-red-700 font-medium">Payment failed</p>
            <Button 
              className="mt-4"
              onClick={() => setStatus('ready')}
            >
              Try Again
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
